import { useState } from 'react'
import { createPortal } from 'react-dom'
import {
  X, Loader2, User, Phone, Mail, MapPin,
  Layers, Database, FileSpreadsheet, IndianRupee, Calendar,
  Server, Cable, Settings, CheckCircle2, ClipboardPaste, Sparkles
} from 'lucide-react'
import { customersService } from '@/services/firebase/customers'
import BuildingPicker from '@/components/BuildingPicker'
import type { BsnlCustomer, BsnlCustomerInput } from '@/types'
import toast from 'react-hot-toast'

interface CustomerFormProps {
  initial?: BsnlCustomer | null
  onClose: () => void
  onSaved: (customer: BsnlCustomer) => void
}

const EMPTY_FORM: BsnlCustomerInput = {
  category: 'Case IV A',
  exchangeCode: 'TVMTPK',
  serviceNumber: '',
  subServiceType: 'BHARAT FIBER COMBO',
  subscriptionPlan: '',
  planPeriod: 'MONTHLY',
  customerName: '',
  billingAccountNo: 0,
  mobile: 0,
  email: '',
  address: '',
  fmc: 499,
  oltIp: '10.215.181.225',
  bbUserId: '',
  voipPassword: '',
  vlan: 0,
  port: 0,
  building: '',
  activationDate: ''
}

// Helper to parse BSNL copy-pasted raw text
const parseBsnlCopyPaste = (text: string): Record<string, string> => {
  const result: Record<string, string> = {}
  
  const lines = text.split('\n')
    .map(line => line.trim())
    .filter(line => line.length > 0)

  const mappings = [
    { key: 'bbUserId', pattern: /Broadband\s+User\s+ID/i },
    { key: 'oltIp', pattern: /OLT\s+IP\s+Address/i },
    { key: 'vlan', pattern: /VLAN\s+ID/i },
    { key: 'port', pattern: /Port/i },
    { key: 'voipPassword', pattern: /VoIP\s+Password/i },
    { key: 'exchangeCode', pattern: /Exchange\s+Code/i },
    { key: 'subscriptionPlan', pattern: /Subscription\s+Plan/i },
    { key: 'fmc', pattern: /Monthly\s+FMC/i },
    { key: 'planPeriod', pattern: /Plan\s+Period/i },
    { key: 'billingAccountNo', pattern: /Billing\s+Account\s+No/i },
    { key: 'subServiceType', pattern: /Service\s+Type/i },
    { key: 'mobile', pattern: /Phone\s+\/\s+Mobile|Mobile\s+Number|Phone\s+Mobile/i },
    { key: 'email', pattern: /Email\s+ID|Email/i },
    { key: 'address', pattern: /Installation\s+Address/i },
    { key: 'activationDate', pattern: /Activation\s+Date/i },
    { key: 'category', pattern: /Category\s+Code|Category/i },
    { key: 'customerName', pattern: /Customer\s+Name|Customer|Name/i },
    { key: 'serviceNumber', pattern: /Service\s+Number|Service\s+No/i }
  ]

  const isKnownLabel = (str: string): boolean => {
    return mappings.some(m => m.pattern.test(str)) || 
           /Connection\s+Configuration/i.test(str) || 
           /Plan\s+&\s+Billing/i.test(str) || 
           /Customer\s+Profile\s+Details/i.test(str)
  }

  for (let i = 0; i < lines.length; i++) {
    const currentLine = lines[i]
    for (const m of mappings) {
      if (m.pattern.test(currentLine)) {
        let foundValue = ''
        for (let j = i + 1; j < lines.length; j++) {
          const val = lines[j]
          if (isKnownLabel(val)) break
          foundValue = val
          break
        }
        if (foundValue) {
          result[m.key] = foundValue
        }
        break
      }
    }
  }

  // Fallback inline key-value parsing (e.g. "OLT IP Address: 10.215...")
  for (const line of lines) {
    if (line.includes(':') || line.includes('=')) {
      const sepIdx = line.indexOf(':') !== -1 ? line.indexOf(':') : line.indexOf('=')
      const label = line.substring(0, sepIdx).trim()
      const value = line.substring(sepIdx + 1).trim()
      if (label && value) {
        for (const m of mappings) {
          if (m.pattern.test(label) && !result[m.key]) {
            result[m.key] = value
          }
        }
      }
    }
  }

  return result
}

export const CustomerForm = ({ initial, onClose, onSaved }: CustomerFormProps) => {
  const [form, setForm] = useState<Record<string, any>>(
    initial ? { ...initial } : { ...EMPTY_FORM }
  )
  const [saving, setSaving] = useState(false)
  const [showSaveConfirm, setShowSaveConfirm] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [pasteMode, setPasteMode] = useState(false)
  const [rawPasteText, setRawPasteText] = useState('')

  const validate = () => {
    const e: Record<string, string> = {}
    if (!form.customerName?.trim()) e.customerName = 'Customer Name is required'
    if (!form.serviceNumber?.trim()) e.serviceNumber = 'Service Number is required'
    if (!form.mobile) e.mobile = 'Mobile number is required'
    else if (!/^\d{10}$/.test(String(form.mobile).replace(/\s/g, ''))) e.mobile = 'Enter a 10-digit mobile number'
    if (!form.address?.trim()) e.address = 'Address is required'
    if (!form.bbUserId?.trim()) e.bbUserId = 'Broadband User ID is required'
    if (!form.oltIp?.trim()) e.oltIp = 'OLT IP address is required'
    return e
  }

  const handleSubmit = () => {
    const e = validate()
    if (Object.keys(e).length) {
      setErrors(e)
      toast.error('Please fix the highlighted errors')
      return
    }
    setShowSaveConfirm(true)
  }

  const executeSubmit = async () => {
    setSaving(true)
    
    // Format payload numeric/optional fields properly
    const payload: BsnlCustomerInput = {
      category: form.category || 'Case IV A',
      exchangeCode: form.exchangeCode || 'TVMTPK',
      serviceNumber: form.serviceNumber.trim(),
      subServiceType: form.subServiceType || 'BHARAT FIBER COMBO',
      subscriptionPlan: form.subscriptionPlan || '',
      planPeriod: form.planPeriod || 'MONTHLY',
      customerName: form.customerName.trim(),
      billingAccountNo: Number(form.billingAccountNo || 0),
      mobile: Number(form.mobile),
      email: form.email || null,
      address: form.address.trim(),
      fmc: Number(form.fmc || 0),
      oltIp: form.oltIp.trim(),
      bbUserId: form.bbUserId.trim(),
      voipPassword: form.voipPassword || null,
      vlan: form.vlan ? Number(form.vlan) : null,
      port: form.port ? Number(form.port) : null,
      building: form.building || '',
      activationDate: form.activationDate || new Date().toLocaleDateString('en-IN') + ' 10:00:00'
    }

    try {
      const result = initial
        ? await customersService.update(initial.id, payload)
        : await customersService.create(payload)
      if (!result.success) throw new Error(result.error)
      toast.success(initial ? 'Customer updated successfully' : 'Customer created successfully')
      
      // Pass saved customer back
      const savedCustomer: BsnlCustomer = {
        id: initial ? initial.id : payload.serviceNumber.trim().replace(/[\s/]/g, '_'),
        ...payload,
        createdAt: initial?.createdAt || new Date(),
        updatedAt: new Date()
      }
      onSaved(savedCustomer)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setSaving(false)
      setShowSaveConfirm(false)
    }
  }

  const handleChange = (key: string, val: any) => {
    setForm(f => ({ ...f, [key]: val }))
    setErrors(er => ({ ...er, [key]: '' }))
  }

  const handleAutofill = () => {
    if (!rawPasteText.trim()) {
      toast.error('Please paste BSNL details first')
      return
    }

    const parsed = parseBsnlCopyPaste(rawPasteText)
    if (Object.keys(parsed).length === 0) {
      toast.error('Could not parse any parameters. Please verify the copied text format.')
      return
    }

    // Post process parsed values
    let mobileNum = parsed.mobile ? parsed.mobile.replace(/\D/g, '') : ''
    if (mobileNum.startsWith('91') && mobileNum.length > 10) {
      mobileNum = mobileNum.substring(2)
    }

    let fmcVal = parsed.fmc ? parsed.fmc.replace(/[^\d]/g, '') : ''
    let vlanVal = parsed.vlan ? parsed.vlan.replace(/\D/g, '') : ''
    let portVal = parsed.port ? parsed.port.replace(/\D/g, '') : ''
    let billingAcc = parsed.billingAccountNo ? parsed.billingAccountNo.replace(/\D/g, '') : ''
    let voipPass = parsed.voipPassword
    if (voipPass?.toLowerCase() === 'none') voipPass = ''

    // Smart Service Number Inference from Broadband User ID
    let inferredServiceNumber = parsed.serviceNumber || ''
    if (!inferredServiceNumber && parsed.bbUserId) {
      const match = parsed.bbUserId.match(/\d+/)
      if (match) {
        const digits = match[0]
        if (digits.length === 10) {
          inferredServiceNumber = `0${digits.substring(0, 3)}-${digits.substring(3)}`
        }
      }
    }

    // Try to guess building/landmark from address
    let guessedBuilding = parsed.building || ''
    if (!guessedBuilding && parsed.address) {
      const addUpper = parsed.address.toUpperCase()
      const landmarks = ['TECHNOPARK', 'NILA', 'GAYATRI', 'TEJASWINI', 'BHAVANI', 'GANGA', 'YAMUNA', 'NIAGARABLDG', 'AMSTOR', 'LEELA', 'KEYSTONE']
      for (const lm of landmarks) {
        if (addUpper.includes(lm)) {
          guessedBuilding = lm
          break
        }
      }
    }

    setForm(prev => ({
      ...prev,
      customerName: parsed.customerName || prev.customerName,
      serviceNumber: inferredServiceNumber || prev.serviceNumber,
      bbUserId: parsed.bbUserId || prev.bbUserId,
      oltIp: parsed.oltIp || prev.oltIp,
      vlan: vlanVal ? Number(vlanVal) : prev.vlan,
      port: portVal ? Number(portVal) : prev.port,
      voipPassword: voipPass || prev.voipPassword,
      exchangeCode: parsed.exchangeCode || prev.exchangeCode,
      subscriptionPlan: parsed.subscriptionPlan || prev.subscriptionPlan,
      fmc: fmcVal ? Number(fmcVal) : prev.fmc,
      planPeriod: parsed.planPeriod || prev.planPeriod,
      billingAccountNo: billingAcc ? Number(billingAcc) : prev.billingAccountNo,
      subServiceType: parsed.subServiceType || prev.subServiceType,
      mobile: mobileNum ? Number(mobileNum) : prev.mobile,
      email: parsed.email || prev.email,
      address: parsed.address || prev.address,
      activationDate: parsed.activationDate || prev.activationDate,
      category: parsed.category || prev.category,
      building: guessedBuilding || prev.building
    }))

    setPasteMode(false)
    setRawPasteText('')
    toast.success('Successfully parsed and autofilled customer details!')
  }

  const iw = "flex items-center gap-2 border border-gray-200 rounded-2xl px-3.5 py-2.5 focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-400 hover:border-gray-300 bg-white/90 transition-all shadow-sm"
  const ic = "flex-1 text-sm outline-none text-gray-900 placeholder-gray-400 bg-transparent"

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4 bg-black/65 backdrop-blur-sm">
      <div className="bg-white rounded-3xl w-full max-w-2xl shadow-2xl overflow-hidden max-h-[85vh] sm:max-h-[90vh] flex flex-col my-auto">
        {/* Header */}
        <div className="px-6 py-4 flex items-center justify-between shrink-0"
          style={{ background: 'linear-gradient(135deg, #050d1f 0%, #0d1f3c 100%)' }}>
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-2xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #3b82f6, #06b6d4)', boxShadow: '0 0 12px rgba(59,130,246,0.4)' }}>
              <User size={16} className="text-white" />
            </div>
            <div>
              <h2 className="font-extrabold text-white text-sm">{initial ? 'Edit Customer Details' : 'Add New BSNL Customer'}</h2>
              <p className="text-[10px] text-blue-300/70">{initial ? 'Modify customer database record' : 'Fill in connection and configuration details'}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Scrollable Form Body */}
        <div className="p-6 overflow-y-auto space-y-5 bg-slate-50 flex-1">
          {/* Quick Paste Assistant */}
          {!initial && (
            <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-200/50 rounded-2xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles size={16} className="text-blue-600 animate-pulse" />
                  <div>
                    <h4 className="text-xs font-bold text-gray-800">BSNL Autofill Assistant</h4>
                    <p className="text-[10px] text-gray-500">Paste raw text from the BSNL portal to save time.</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setPasteMode(!pasteMode)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-[11px] font-black rounded-xl transition-all active:scale-95 shadow-sm"
                >
                  <ClipboardPaste size={12} />
                  {pasteMode ? 'Cancel Paste' : 'Autofill from Text'}
                </button>
              </div>

              {pasteMode && (
                <div className="mt-3 space-y-2.5 animate-fade-in">
                  <textarea
                    rows={4}
                    value={rawPasteText}
                    onChange={e => setRawPasteText(e.target.value)}
                    placeholder="Paste Connection Configuration, Plan & Billing details, and Profile Details here..."
                    className="w-full text-xs font-mono p-3 bg-white border border-gray-200 rounded-xl outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/10 resize-none shadow-inner leading-relaxed"
                  />
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={handleAutofill}
                      className="px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white text-[11px] font-black rounded-xl transition-all shadow-md active:scale-95"
                    >
                      Parse & Fill Form
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Section 1: Personal Info */}
          <div>
            <h3 className="text-xs font-black text-blue-600 uppercase tracking-widest mb-3">1. Personal & Contact Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[11px] font-black text-gray-400 uppercase tracking-wider mb-1.5">Customer Name *</label>
                <div className={iw}>
                  <User size={14} className="text-gray-400 shrink-0" />
                  <input value={form.customerName || ''} onChange={e => handleChange('customerName', e.target.value)} placeholder="e.g. Allianz Helpdesk / Acme Corp" className={ic} />
                </div>
                {errors.customerName && <p className="text-xs text-red-500 mt-1 font-medium">{errors.customerName}</p>}
              </div>

              <div>
                <label className="block text-[11px] font-black text-gray-400 uppercase tracking-wider mb-1.5">Mobile Number *</label>
                <div className={iw}>
                  <Phone size={14} className="text-gray-400 shrink-0" />
                  <input type="number" value={form.mobile || ''} onChange={e => handleChange('mobile', e.target.value)} placeholder="9876543210" className={ic} />
                </div>
                {errors.mobile && <p className="text-xs text-red-500 mt-1 font-medium">{errors.mobile}</p>}
              </div>

              <div>
                <label className="block text-[11px] font-black text-gray-400 uppercase tracking-wider mb-1.5">Email Address</label>
                <div className={iw}>
                  <Mail size={14} className="text-gray-400 shrink-0" />
                  <input type="email" value={form.email || ''} onChange={e => handleChange('email', e.target.value)} placeholder="name@example.com" className={ic} />
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-black text-gray-400 uppercase tracking-wider mb-1.5">Building Name / Landmark</label>
                <BuildingPicker
                  value={form.building || ''}
                  onChange={val => handleChange('building', val)}
                  inputWrapClass={iw}
                  inputClass={ic}
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-[11px] font-black text-gray-400 uppercase tracking-wider mb-1.5">Installation Address *</label>
                <div className="flex border border-gray-200 rounded-2xl px-3.5 py-2.5 focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-400 hover:border-gray-300 bg-white/90 transition-all shadow-sm">
                  <MapPin size={14} className="text-gray-400 shrink-0 mt-1" />
                  <textarea rows={2} value={form.address || ''} onChange={e => handleChange('address', e.target.value)} placeholder="Full physical installation location address" className="flex-1 text-sm outline-none text-gray-900 placeholder-gray-400 bg-transparent resize-none ml-2 leading-relaxed" />
                </div>
                {errors.address && <p className="text-xs text-red-500 mt-1 font-medium">{errors.address}</p>}
              </div>
            </div>
          </div>

          <hr className="border-gray-250/60" />

          {/* Section 2: Technical FTTH Configuration */}
          <div>
            <h3 className="text-xs font-black text-purple-600 uppercase tracking-widest mb-3">2. FTTH Technical Configuration</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[11px] font-black text-gray-400 uppercase tracking-wider mb-1.5">Broadband USER ID *</label>
                <div className={iw}>
                  <User size={14} className="text-gray-400 shrink-0" />
                  <input value={form.bbUserId || ''} onChange={e => handleChange('bbUserId', e.target.value)} placeholder="e.g. al4712117530_sid" className={ic} />
                </div>
                {errors.bbUserId && <p className="text-xs text-red-500 mt-1 font-medium">{errors.bbUserId}</p>}
              </div>

              <div>
                <label className="block text-[11px] font-black text-gray-400 uppercase tracking-wider mb-1.5">BSNL Service Number *</label>
                <div className={iw}>
                  <Layers size={14} className="text-gray-400 shrink-0" />
                  <input value={form.serviceNumber || ''} onChange={e => handleChange('serviceNumber', e.target.value)} placeholder="e.g. 0471-2117530" className={ic} />
                </div>
                {errors.serviceNumber && <p className="text-xs text-red-500 mt-1 font-medium">{errors.serviceNumber}</p>}
              </div>

              <div>
                <label className="block text-[11px] font-black text-gray-400 uppercase tracking-wider mb-1.5">OLT IP Address *</label>
                <div className={iw}>
                  <Server size={14} className="text-gray-400 shrink-0" />
                  <input value={form.oltIp || ''} onChange={e => handleChange('oltIp', e.target.value)} placeholder="10.215.181.225" className={ic} />
                </div>
                {errors.oltIp && <p className="text-xs text-red-500 mt-1 font-medium">{errors.oltIp}</p>}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-black text-gray-400 uppercase tracking-wider mb-1.5">VLAN ID</label>
                  <div className={iw}>
                    <Cable size={14} className="text-gray-400 shrink-0" />
                    <input type="number" value={form.vlan || ''} onChange={e => handleChange('vlan', e.target.value)} placeholder="431" className={ic} />
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] font-black text-gray-400 uppercase tracking-wider mb-1.5">Port Number</label>
                  <div className={iw}>
                    <Cable size={14} className="text-gray-400 shrink-0" />
                    <input type="number" value={form.port || ''} onChange={e => handleChange('port', e.target.value)} placeholder="4" className={ic} />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-black text-gray-400 uppercase tracking-wider mb-1.5">VoIP Password</label>
                <div className={iw}>
                  <Settings size={14} className="text-gray-400 shrink-0" />
                  <input value={form.voipPassword || ''} onChange={e => handleChange('voipPassword', e.target.value)} placeholder="e.g. 1234 or None" className={ic} />
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-black text-gray-400 uppercase tracking-wider mb-1.5">Exchange Code</label>
                <div className={iw}>
                  <Settings size={14} className="text-gray-400 shrink-0" />
                  <input value={form.exchangeCode || ''} onChange={e => handleChange('exchangeCode', e.target.value)} placeholder="TVMTPK" className={ic} />
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-black text-gray-400 uppercase tracking-wider mb-1.5">Category Code</label>
                <div className={iw}>
                  <Settings size={14} className="text-gray-400 shrink-0" />
                  <input value={form.category || ''} onChange={e => handleChange('category', e.target.value)} placeholder="Case III" className={ic} />
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-black text-gray-400 uppercase tracking-wider mb-1.5">Activation Date</label>
                <div className={iw}>
                  <Calendar size={14} className="text-gray-400 shrink-0" />
                  <input value={form.activationDate || ''} onChange={e => handleChange('activationDate', e.target.value)} placeholder="DD/MM/YYYY hh:mm:ss" className={ic} />
                </div>
              </div>
            </div>
          </div>

          <hr className="border-gray-250/60" />

          {/* Section 3: Plan & Billing */}
          <div>
            <h3 className="text-xs font-black text-indigo-600 uppercase tracking-widest mb-3">3. Service & Billing Plan</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-[11px] font-black text-gray-400 uppercase tracking-wider mb-1.5">Subscription Plan</label>
                <div className={iw}>
                  <FileSpreadsheet size={14} className="text-gray-400 shrink-0" />
                  <input value={form.subscriptionPlan || ''} onChange={e => handleChange('subscriptionPlan', e.target.value)} placeholder="e.g. FIBRE/AIRFIBRE SUPER STAR PREMIUM PLUS OTT NEW-FBB-COMBO" className={ic} />
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-black text-gray-400 uppercase tracking-wider mb-1.5">Billing Account No.</label>
                <div className={iw}>
                  <Database size={14} className="text-gray-400 shrink-0" />
                  <input type="number" value={form.billingAccountNo || ''} onChange={e => handleChange('billingAccountNo', e.target.value)} placeholder="9041365663" className={ic} />
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-black text-gray-400 uppercase tracking-wider mb-1.5">Monthly FMC Charges (Rs.)</label>
                <div className={iw}>
                  <IndianRupee size={14} className="text-gray-400 shrink-0" />
                  <input type="number" value={form.fmc || ''} onChange={e => handleChange('fmc', e.target.value)} placeholder="999" className={ic} />
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-black text-gray-400 uppercase tracking-wider mb-1.5">Plan Period</label>
                <div className={iw}>
                  <Calendar size={14} className="text-gray-400 shrink-0" />
                  <select value={form.planPeriod || 'MONTHLY'} onChange={e => handleChange('planPeriod', e.target.value)} className="flex-1 text-sm outline-none text-gray-900 bg-transparent">
                    <option value="MONTHLY">Monthly</option>
                    <option value="13MONTHLY">13-Monthly (Annual)</option>
                    <option value="YEARLY">Yearly</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-black text-gray-400 uppercase tracking-wider mb-1.5">Service Type</label>
                <div className={iw}>
                  <Layers size={14} className="text-gray-400 shrink-0" />
                  <input value={form.subServiceType || ''} onChange={e => handleChange('subServiceType', e.target.value)} placeholder="BHARAT FIBER COMBO" className={ic} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-150 flex gap-3 shrink-0 bg-white">
          <button onClick={onClose} className="flex-1 py-3 border border-gray-200 rounded-2xl text-sm font-bold text-gray-600 hover:bg-slate-50 transition-colors">
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={saving}
            className="flex-1 py-3 text-white rounded-2xl text-sm font-black transition-all disabled:opacity-60 flex items-center justify-center gap-2 active:scale-[0.98]"
            style={{ background: 'linear-gradient(135deg, #2563eb, #1d4ed8)', boxShadow: '0 4px 16px rgba(37,99,235,0.3)' }}>
            {initial ? 'Save Changes' : 'Create Customer'}
          </button>
        </div>
      </div>

      {/* Save Confirmation Dialog */}
      {showSaveConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40 backdrop-blur-[2px]">
          <div className="bg-white rounded-3xl w-full max-w-sm p-6 flex flex-col items-center text-center gap-4 shadow-xl border border-gray-150 animate-fade-in">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-blue-50 border border-blue-100 text-blue-500">
              <CheckCircle2 size={24} />
            </div>
            <div>
              <p className="font-extrabold text-gray-900 text-base">Confirm Save Details</p>
              <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">
                Are you sure you want to save customer record details for <span className="font-bold text-gray-800">{form.customerName}</span>?
              </p>
            </div>
            <div className="flex gap-3 w-full mt-2">
              <button 
                onClick={() => setShowSaveConfirm(false)} 
                className="flex-1 py-2.5 border border-gray-200 rounded-xl text-xs font-bold text-gray-600 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button 
                onClick={executeSubmit} 
                disabled={saving}
                className="flex-1 py-2.5 text-white rounded-xl text-xs font-black bg-blue-600 hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-50 flex items-center justify-center gap-1.5"
              >
                {saving && <Loader2 size={13} className="animate-spin" />}
                {saving ? 'Saving...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  )
}
export default CustomerForm
